import React, { FunctionComponent, useMemo } from "react";
import { ObjectList } from "../../../coobjects/object-list-panel/ObjectList";
import { CoObjectType } from "../../../interfaces/CoObject";
import { selectContentControl } from "../../../../office/ContentControl";
import { AutocompleteTextField } from "../../../generic/AutocompleteTextField";
import { MessageBar, MessageBarType, Modal } from "@fluentui/react";
import HeaderPanel from "../../../layout/HeaderPanel";
import { formatMessage } from "../../../../utils/helpers/LanguageHelper";
import FooterPanel from "../../../layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { contentStyles } from "../../../layout/ActionModal";
import { FilterItem } from "../../../generic/filterBar/FilterBar";
import { CoObjectTypeEnum } from "../../../../api/models";
import { AddTagProps, useAddTagModal } from "./AddTagModalHook";
import { CAPTION_ADD_TAG_MODAL_PENDING_AGENT_EDIT } from "../validationCaptions";

const getObjectIcon = (type: CoObjectType | CoObjectTypeEnum) => {
  // eslint-disable-next-line react/display-name
  return () => <span className={`${type} icon_class`} />;
};

const getTagObjectFilters: () => FilterItem[] = () => [
  {
    key: "all",
    label: formatMessage("cof.objectlist.filters.allobjects"),
    value: undefined,
  },
  {
    key: "amount",
    label: formatMessage("cof.createnewobject.amount"),
    value: [CoObjectTypeEnum.AMOUNT, CoObjectTypeEnum.AMOUNT_AIH],
    icon: getObjectIcon(CoObjectTypeEnum.AMOUNT),
  },
  {
    key: "table",
    label: formatMessage("cof.createnewobject.table"),
    value: CoObjectTypeEnum.TABLE,
    icon: getObjectIcon(CoObjectTypeEnum.TABLE),
  },
];

export const AddTagModal: FunctionComponent<AddTagProps> = (props) => {
  const {
    listObjects,
    setSelectedObjects,
    selectedCellItems,
    preselectedCellsMap,
    autocompletes,
    tagLabel,
    setTagLabel,
    handleBack,
    handleSave,
    selectedTag,
    hasPendingAgentEdit,
  } = useAddTagModal(props);

  const tagObjectFilters = useMemo(() => getTagObjectFilters(), []);

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={props.isShown}
      containerClassName={contentStyles.container}
      styles={{
        scrollableContent: {
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          paddingBottom: "70px",
          boxSizing: "border-box",
        },
      }}
    >
      <HeaderPanel applyPadding>{formatMessage("cof.validation.title.tagcreation")}</HeaderPanel>
      {hasPendingAgentEdit && (
        <MessageBar messageBarType={MessageBarType.warning}>
          {formatMessage(CAPTION_ADD_TAG_MODAL_PENDING_AGENT_EDIT)}
        </MessageBar>
      )}
      <div
        style={{
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          padding: "0 0.5rem",
        }}
      >
        <span
          style={{
            fontWeight: "bold",
            color: "var(--color-blue)",
            fontSize: "14px",
            marginTop: "2px",
          }}
        >
          {formatMessage("cof.validation.title.objectselection")}
        </span>
        <span
          style={{
            color: "var(--color-grey)",
            backgroundColor: "white",
            zIndex: "1000",
            fontSize: "12px",
            marginBottom: "-5px",
          }}
        >
          {formatMessage("cof.validation.info.objectselection")}
        </span>
        <ObjectList
          key={String(props.isShown)}
          objects={listObjects}
          multiselectable={true}
          expandableObjects={true}
          allowFiltering={true}
          disableSelectAll={true}
          onItemClick={(item) => {
            void selectContentControl(item);
          }}
          onSelectionChange={setSelectedObjects}
          style={{ overflowY: "overlay", height: "100%" }}
          objectFilters={tagObjectFilters}
          preselectedCellsMap={preselectedCellsMap}
        />
      </div>
      <hr style={{ margin: "5px 0 0 0" }} />
      <div style={{ padding: "0 0.5rem" }}>
        <div
          style={{
            fontWeight: "bold",
            color: "var(--color-blue)",
            fontSize: "14px",
          }}
        >
          {formatMessage("cof.validation.title.tagname")}
        </div>
        <div
          style={{
            color: "var(--color-grey)",
            fontSize: "12px",
            marginBottom: "4px",
          }}
        >
          {formatMessage("cof.validation.info.tagname")}
        </div>
        <AutocompleteTextField
          autocompleteList={autocompletes}
          allowFreeInput={true}
          value={tagLabel}
          onChange={(_e, v) => setTagLabel(v)}
          noOptionsText="No matches"
          openOnFocus={!selectedTag}
        />
      </div>
      <FooterPanel>
        <PrimaryButton id="back_button" data-testid="back" onClick={handleBack} text={formatMessage("cof.base.back")} />
        <PrimaryButton
          id="confirm_button"
          data-testid="save"
          onClick={handleSave}
          text={formatMessage("cof.base.save")}
          disabled={
            !listObjects ||
            (listObjects.every((o) => !o.selected) && selectedCellItems.length === 0) ||
            !tagLabel ||
            tagLabel == ""
          }
          style={{ float: "right" }}
        />
      </FooterPanel>
    </Modal>
  );
};
